import { useEffect, useRef, useState } from 'react';
import {
  cloudConfig, speechBubbleCloudConfig, getAnimationSpeed, computeReadingTime, wordCountOf
} from '@services/transient-thought.service';
import { getColor } from '@services/utils.service';

export const transientThoughtVariants = {
  bubbles: 'bubbles',
  cloud: 'cloud',
  speechBubble: 'speechBubble'
};

const rng = (min, max, exclude) => {
  let n;
  do n = Math.floor(Math.random() * (max - min) + min);
  while (n === exclude);
  return n;
};

/**
 * The live two-slot (master/slave) transient-thought animation engine.
 *
 * The whole chain runs from a single self-rescheduling timer and reads every
 * input from a ref, so it never stalls on a stale closure and always continues
 * through every queued comment — even while display style / colors / playback
 * speed change underneath it.
 *
 * Decoupled from Firestore: feed it comments with `enqueue(commentList)` and
 * pass `onConsume(rawComment)` to be told when a bubble starts leaving (used by
 * the caller to arrayRemove it from the backing doc).
 */
export function useTransientThoughtChain({
  playbackSpeed = 'default', feature, colors, onConsume
}) {
  const [slotA, setSlotA] = useState(null);
  const [slotB, setSlotB] = useState(null);

  // Append-only queue; items are shift()ed by showNext.
  const queue = useRef([]);
  // Ids handed to a slot — incoming snapshots containing them are ignored.
  const consumedIds = useRef(new Set());
  // Ids ever queued — stops re-queueing comments still present in the source.
  const seenIds = useRef(new Set());

  const isPausedRef = useRef(false);
  // True when the chain has stopped (queue drained or paused).
  const isIdleRef = useRef(true);
  const activeSlotRef = useRef('b');
  const chainTimerRef = useRef(null);
  const slotARef = useRef(null);
  const slotBRef = useRef(null);
  const counter = useRef(0);
  // Holds the last comment kept visible on an otherwise-empty stage, so it can
  // be faded out when the next comment arrives instead of leaving a blank screen.
  const parkedRef = useRef(null);

  // Fresh copies of inputs for the timer-driven code.
  const playbackRef = useRef(playbackSpeed);
  const featureRef = useRef(feature);
  const colorsRef = useRef(colors);
  const onConsumeRef = useRef(onConsume);
  useEffect(() => { playbackRef.current = playbackSpeed; }, [playbackSpeed]);
  useEffect(() => { featureRef.current = feature; }, [feature]);
  useEffect(() => { colorsRef.current = colors; }, [colors]);
  useEffect(() => { onConsumeRef.current = onConsume; }, [onConsume]);

  const syncSlotA = (cfg) => { slotARef.current = cfg; setSlotA(cfg); };
  const syncSlotB = (cfg) => { slotBRef.current = cfg; setSlotB(cfg); };

  const buildConfig = (comment, slot) => {
    const speed = getAnimationSpeed(playbackRef.current);
    const words = wordCountOf(comment.words || '');
    const rTime = computeReadingTime(words, playbackRef.current);
    const side = slot === 'a' ? 'Right' : 'Left';
    const point = ['top', 'bottom'][rng(0, 2, -1)];
    const posKey = `${point}${side}`;

    const liveFeature = featureRef.current;
    const isBubble = liveFeature.setting.transientThoughtDisplayStyle
      === transientThoughtVariants.bubbles;
    const base = isBubble ? speechBubbleCloudConfig[posKey] : cloudConfig[posKey];

    return {
      ...base,
      // Stamp the position slot so a parked bubble can be rebuilt in a different
      // display style (its SVG ids/base config are looked up by posKey).
      posKey,
      words: comment.words,
      limit: comment.limit,
      username: comment.username,
      rawComment: comment,
      readingTime: rTime,
      // Stamp the style this config's SVG ids belong to. The renderer must use
      // this — not the live setting — so a bubble already on screen when the
      // user switches display style keeps rendering with its original SVG set
      // (cloud ids tr/bl have no speech-bubble variant and would crash).
      isBubble,
      transition: { duration: speed.start },
      fillColor: getColor(colorsRef.current, counter.current++, liveFeature.color.ttBgColor)
    };
  };

  // Rebuild the parked (lingering last) bubble in the current display style, so
  // switching styles updates the message left on screen too. Safe because the
  // new base config (and its SVG ids) is looked up fresh by posKey — unlike a
  // mid-flight bubble, a parked one is static so there is no in-progress
  // transition to conflict with.
  const restyleParked = () => {
    const parked = parkedRef.current;
    if (!parked) return;
    const { slot, snapshot } = parked;
    const isBubble = featureRef.current.setting.transientThoughtDisplayStyle
      === transientThoughtVariants.bubbles;
    if (isBubble === snapshot.isBubble) return;
    const base = isBubble
      ? speechBubbleCloudConfig[snapshot.posKey]
      : cloudConfig[snapshot.posKey];
    if (!base) return;
    const restyled = {
      ...base,
      posKey: snapshot.posKey,
      words: snapshot.words,
      limit: snapshot.limit,
      username: snapshot.username,
      rawComment: snapshot.rawComment,
      readingTime: snapshot.readingTime,
      isBubble,
      transition: snapshot.transition,
      fillColor: snapshot.fillColor
    };
    parkedRef.current = { slot, snapshot: restyled };
    if (slot === 'a') syncSlotA(restyled);
    else syncSlotB(restyled);
  };

  // Runs after the featureRef sync effect above, so restyleParked sees the new
  // display style. Keeps the lingering last bubble in step with the setting.
  useEffect(() => {
    restyleParked();
  }, [feature.setting.transientThoughtDisplayStyle]);

  // The single driving function for the chain. Reads exclusively from refs.
  const showNext = () => {
    clearTimeout(chainTimerRef.current);

    if (isPausedRef.current || !queue.current.length) {
      isIdleRef.current = true;
      return;
    }
    isIdleRef.current = false;

    // A previous "last" bubble may be parked on screen (kept visible rather than
    // faded to an empty stage). Now that a new comment is arriving to replace it,
    // fade the parked bubble out.
    if (parkedRef.current) {
      const { slot: pSlot, snapshot: pSnap } = parkedRef.current;
      parkedRef.current = null;
      const pSpeed = getAnimationSpeed(playbackRef.current);
      const pFade = {
        animate: { scale: 1, opacity: 0, x: 0, y: 0 },
        transition: { duration: pSpeed.end, delay: 0 }
      };
      if (pSlot === 'a') syncSlotA({ ...pSnap, ...pFade });
      else syncSlotB({ ...pSnap, ...pFade });
      setTimeout(() => {
        if (pSlot === 'a' && slotARef.current === pSnap) syncSlotA(null);
        else if (pSlot === 'b' && slotBRef.current === pSnap) syncSlotB(null);
      }, pSpeed.end * 1000);
    }

    const comment = queue.current.shift();
    const key = comment?.id || comment?.words;
    if (key) consumedIds.current.add(key);

    const speed = getAnimationSpeed(playbackRef.current);
    const words = wordCountOf(comment.words || '');
    const rTime = computeReadingTime(words, playbackRef.current);

    const slot = activeSlotRef.current === 'a' ? 'b' : 'a';
    activeSlotRef.current = slot;

    const config = buildConfig(comment, slot);
    if (slot === 'a') syncSlotA(config);
    else syncSlotB(config);

    // After grow-in (speed.start) + on-screen reading time: fade out, then advance.
    chainTimerRef.current = setTimeout(() => {
      const snapshot = slot === 'a' ? slotARef.current : slotBRef.current;
      if (!snapshot) return; // slot was cleared (e.g. reset mid-flight)

      // Tell the caller this comment has been displayed (→ arrayRemove).
      onConsumeRef.current?.(snapshot.rawComment);

      // If this is the last comment and we're live, keep it on screen instead of
      // fading to an empty stage. It's "parked" and fades out only when the next
      // comment arrives (handled at the top of showNext).
      if (!queue.current.length && !isPausedRef.current) {
        parkedRef.current = { slot, snapshot };
        isIdleRef.current = true;
        return;
      }

      // Fade out immediately — rTime already accounts for full reading time
      // (including a linger bonus for long text), so no extra hold delay here.
      const fade = {
        animate: { scale: 1, opacity: 0, x: 0, y: 0 },
        transition: { duration: speed.end, delay: 0 }
      };
      if (slot === 'a') syncSlotA({ ...snapshot, ...fade });
      else syncSlotB({ ...snapshot, ...fade });

      // Clear the slot after the fade completes.
      setTimeout(() => {
        if (slot === 'a' && slotARef.current === snapshot) syncSlotA(null);
        else if (slot === 'b' && slotBRef.current === snapshot) syncSlotB(null);
      }, speed.end * 1000);

      // Advance to the next comment once the current bubble is partway through
      // its fade — so the new bubble grows in while the old one is still visibly
      // fading away, rather than both transitions starting at the same instant.
      chainTimerRef.current = setTimeout(showNext, (speed.end / 2) * 1000);
    }, (speed.start + rTime) * 1000);
  };

  // Append genuinely-new comments from a source list and start the chain if idle.
  const enqueue = (commentList = []) => {
    const newComments = commentList.filter((c) => {
      const key = c?.id || c?.words;
      if (!key) return false;
      if (consumedIds.current.has(key)) return false; // already displayed
      if (seenIds.current.has(key)) return false; // already queued
      seenIds.current.add(key);
      return true;
    });
    if (newComments.length > 0) {
      queue.current.push(...newComments);
      if (isIdleRef.current && !isPausedRef.current) showNext();
    }
  };

  const reset = () => {
    clearTimeout(chainTimerRef.current);
    queue.current = [];
    consumedIds.current = new Set();
    seenIds.current = new Set();
    isIdleRef.current = true;
    activeSlotRef.current = 'b';
    slotARef.current = null;
    slotBRef.current = null;
    parkedRef.current = null;
    counter.current = 0;
    setSlotA(null);
    setSlotB(null);
  };

  // Pause spawning (live area scrolled out of view) / resume with backlog.
  const setPaused = (paused) => {
    isPausedRef.current = paused;
    if (!paused && isIdleRef.current && queue.current.length > 0) showNext();
  };

  // Belt-and-suspenders slot clearing tied to the Framer Motion fade completion.
  const disableSlotA = ({ opacity }) => {
    if (!opacity) { slotARef.current = null; setSlotA(null); }
  };
  const disableSlotB = ({ opacity }) => {
    if (!opacity) { slotBRef.current = null; setSlotB(null); }
  };

  // Cancel any pending timer on unmount.
  useEffect(() => () => clearTimeout(chainTimerRef.current), []);

  return { slotA, slotB, enqueue, reset, setPaused, disableSlotA, disableSlotB };
}
